import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { Position } from '@core/http-types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  pipeState,
} from '@core/ngrx-legacy';
import type { TalentPool, TalentPoolData } from '@core/talent-pools-types';

import { TalentPoolsScope } from '../types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<TalentPool>()(
  'TalentPoolsItem',
  {
    customActions: adapter => {
      const actions = getEntityCollectionActions(adapter, ({ id }) => id!);
      return {
        fetchCollection: (state, payload: { scope: TalentPoolsScope }) =>
          adapter.markAsPending(state),
        updatePositions: (state, payload: { positions: Position[] }) =>
          pipeState(state, next =>
            adapter.markAsPending(next, { scope: TalentPoolsScope.Default, entities: [] }),
          ),
        updatePositionSuccess: (state, payload: Position[]) => {
          const updated = [...payload]
            .sort((a, b) => b.position - a.position)
            .map(({ id, position }) => ({
              ...state.entityMap[id]!,
              position: position,
            }));
          return pipeState(
            state,
            next => adapter.setMany(next, updated, { scope: TalentPoolsScope.Default }),
            next => adapter.markAsLoaded(next, { scope: TalentPoolsScope.Default, entities: [] }),
          );
        },
        updatePositionsFailed: (state, payload: { error: HttpErrorResponse }) =>
          pipeState(state, next =>
            adapter.setError(next, payload.error, {
              scope: TalentPoolsScope.Default,
              entities: [],
            }),
          ),
        updateOnePessimistic: (state, payload: { data: TalentPoolData & { id: number } }) =>
          adapter.markAsPending(state),
        updateOnePessimisticSuccess: (state, payload: { data: TalentPool }) =>
          actions.updateOnePessimisticSuccess(state, payload),
        updateOnePessimisticFailed: (
          state,
          payload: { id: number | string; error: HttpErrorResponse },
        ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),

        upsertOne: (state, payload: { data: TalentPool; scope: TalentPoolsScope }) =>
          adapter.upsertOne(state, payload.data, { scope: payload.scope }),
      };
    },
  },
);

const byIdOrSlug = memoizeSelector((identifier: number | string) =>
  createSelector(selectors.allEntities, (state: TalentPool[]) =>
    state.find(offer => offer.slug === identifier || offer.id === identifier),
  ),
);

const metaByIdOrSlug = memoizeSelector((identifier: number | string) => selectors.meta(identifier));

const TalentPoolsItemSelectors = {
  ...selectors,
  entities: memoizeSelector((archived: boolean = false) =>
    selectors.entities({ scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default }),
  ),
  byIdOrSlug,
  metaByIdOrSlug,
};

export {
  TalentPoolsItemSelectors,
  actions as TalentPoolsItemActions,
  reducer as TalentPoolsItemReducer,
  namespace as TalentPoolsItemNamespace,
};
